import React, { useState } from 'react';

import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle, Stack, TextField } from '@mui/material';

import { useExportGHAParticipationReport } from '~/hooks/apis/public-offer-feed/useGHAParticipationReport';

import { downloadCsvFile } from '~/utils/csvDownload';

import {
  DEFAULT_EXPORT_ROWS,
  EXPORT_HEADERS,
  ExportQuery,
  MAX_EXPORT_ROWS,
  getExportFilename,
  parseExportRowCount,
  toExportRecords,
} from './exportReport';

interface Props {
  query: ExportQuery;
}

export default function ExportCsvButton({ query }: Props) {
  const [open, setOpen] = useState(false);
  const [rowCountInput, setRowCountInput] = useState(String(DEFAULT_EXPORT_ROWS));
  const exportReport = useExportGHAParticipationReport();

  const rowCount = parseExportRowCount(rowCountInput);

  const handleClose = () => {
    if (exportReport.isPending) {
      return;
    }
    setOpen(false);
    exportReport.reset();
  };

  const handleExport = () => {
    if (rowCount === null || exportReport.isPending) {
      return;
    }
    exportReport.mutate(
      { query, rowCount },
      {
        onSuccess: (rows) => {
          downloadCsvFile(toExportRecords(rows), EXPORT_HEADERS, getExportFilename(query, rows.length));
          setOpen(false);
          exportReport.reset();
        },
      },
    );
  };

  return (
    <>
      <Button variant="text" startIcon={<FileDownloadIcon />} onClick={() => setOpen(true)} sx={{ ml: 'auto' }}>
        Export CSV
      </Button>

      <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
        <DialogTitle>Export to CSV</DialogTitle>
        <DialogContent>
          <Stack spacing={2} pt={1}>
            <TextField
              autoFocus
              label="How many rows?"
              type="number"
              value={rowCountInput}
              onChange={(event) => setRowCountInput(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  handleExport();
                }
              }}
              disabled={exportReport.isPending}
              error={rowCount === null}
              helperText={
                rowCount === null
                  ? `Enter a whole number from 1 to ${MAX_EXPORT_ROWS.toLocaleString()}.`
                  : 'Top rows by the current sort, date range and filters.'
              }
              slotProps={{ htmlInput: { min: 1, max: MAX_EXPORT_ROWS, step: 1 } }}
            />
            {exportReport.error && <Alert severity="error">Couldn't export: {exportReport.error.message}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button variant="text" onClick={handleClose} disabled={exportReport.isPending}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleExport} disabled={rowCount === null || exportReport.isPending}>
            {exportReport.isPending ? 'Exporting…' : 'Export'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
